import { ApiUtil } from 'lecom-api';
import { ActivityFormEventHandler, ActivityFormEvents } from 'process/events/activity_form_event_handler';
import { FormEvents, FormEventHandler } from 'form/events/form_event_handler';
import PaginationHandler from 'realize_overrides/handlers/pagination_handler';
import LecomReflux from 'reflux/lecom_reflux';
import { generateUUID } from 'utils/uuid';
import GridFormStoreItem from '../models/grid_form_store_item';
import GridFormValuesLoader from '../datastore/grid_form_values_loader';

import {
	InsertRowHandler, EditRowHandler, UpdateRowHandler,
	RemoveRowHandler, ResetHandler, SubmitRowHandler
} from '../handlers';

export const GridFormActions = LecomReflux.createActions([
	'init',
	'loadPage',
	'loadNextPage',
	'loadNextPageData',
	'insertPageData',
	'editRow',
	'insertDataRow',
	'updateDataRow',
	'removeDataRow',
	'submitDataRow',
	'loadInitialValues',
	'reset',
	'updateDataRowSign'
]);

export const GridFormStore = LecomReflux.createStore({
	listenables: GridFormActions,
	storeItemClass: GridFormStoreItem,
	eventHandler: FormEventHandler,
	events: FormEvents,

	postConstruct(storeItem) {
		if (storeItem.loadFirstPage) this.onLoadPage(storeItem.name, 1);
		if (!this.initialized) this.subscribeToProcessSaveEvent();
	},

	onLoadPage(gridFormName, page = 1) {
		const { loadedPages } = this.store[gridFormName];

		loadedPages.includes(page)
			? this.updateStoreState(gridFormName)
			: this.loadPageFromDataStore(gridFormName, page);
	},

	onLoadNextPage(gridFormName) {
		const { loadedPages } = this.store[gridFormName];
		const nextPages = loadedPages
			.map(page => page + 1)
			.filter(page => loadedPages.indexOf(page) < 0);

		this.onLoadPage(gridFormName, nextPages.length > 0 ? Math.min(...nextPages) : 1);
	},

	onInsertPageData(gridFormName, page, dataRows, isLoadAction=false) {
		const gridFormStore = this.store[gridFormName];
		const pageDataIsNotEmpty = Array.isArray(dataRows) && dataRows.length > 0;

		if (pageDataIsNotEmpty) {
			this.addLoadedPage(gridFormStore, page);
			this.addDataRows(gridFormStore, dataRows);
		}

		this.updateStoreState(gridFormName, pageDataIsNotEmpty && isLoadAction);
	},

	onInsertDataRow(gridFormName, dataRow) {
		const { dataRows, dataRowIdField, commands, totalCount } = this.store[gridFormName];

		const result = InsertRowHandler.execute({ dataRow, dataRows, dataRowIdField }, totalCount, commands);

		this.onSetProperties(gridFormName, result, false);
		this.updateStoreState(gridFormName, false);
	},

	onEditRow(gridFormName, dataRowId, dataRow) {
		const result = EditRowHandler.execute(gridFormName, { dataRowId, dataRow });
		this.onSetProperties(gridFormName, result);
	},

	onUpdateDataRow(gridFormName, dataRow) {
		const { commands, dataRows, dataRowIdField } = this.store[gridFormName];

		const result = UpdateRowHandler.execute(commands, { dataRow, dataRows, dataRowIdField });

		this.onSetProperties(gridFormName, result, false);
		this.updateStoreState(gridFormName, false);
	},

	onRemoveDataRow(gridFormName, dataRowId) {
		this._removeDataRow(gridFormName, dataRowId);
	},

	_removeDataRow(gridFormName, dataRowId) {
		const { dataRowIdField, dataRows, totalCount, commands, propsRows, gridPerPage, gridPage, formAction } = this.store[gridFormName];
		const result = RemoveRowHandler.execute({ dataRows, dataRowId, dataRowIdField}, totalCount, commands, propsRows, gridPerPage, gridPage, gridFormName);
		const gridFormFieldAPI = ApiUtil.getFieldAPI(gridFormName);

		result
			.then(res => {
				this.onSetProperties(gridFormName, res.result, false);
				this.updateStoreState(gridFormName, false, true);

				gridFormFieldAPI.publish(FormEvents.GRID_DELETE_AFTER, res.deletedDataRow, res.result.dataRows);

				if (formAction === 'update')
					this.onReset(gridFormName);
			})
			.catch(err => {
				const formApi = ApiUtil.getFormAPI();

				if (typeof err === "string") {
					formApi.errors({ [gridFormName]: err }).apply();
				}
				else if (err) {
					formApi.errors(err).apply();
				}
			});
	},

	_buildFieldsGrid(gridFormName) {
		const { selectedRowId, clientSideIdField } = this.store[gridFormName];
		let fieldsGrid = ApiUtil.getFieldAPI(gridFormName).fields();
		let fieldsSerializeds = {};

		for (let cont = 0, len = fieldsGrid.length; cont < len; cont++) {
			let currentField = fieldsGrid[cont];
			let currentValue = currentField.value();

			if (!currentValue && currentValue != null)
				currentField.value(null);

			if (!currentField.hidden())
				fieldsSerializeds[currentField.id] = currentField.serialize();
		}

		fieldsSerializeds[clientSideIdField] = selectedRowId;

		return fieldsSerializeds;
	},

	onSubmitDataRow(gridFormName, submittedDataRow) {
		submittedDataRow = submittedDataRow ? submittedDataRow : this._buildFieldsGrid(gridFormName);

		const gridFormFieldAPI = ApiUtil.getFieldAPI(gridFormName);
		const { totalCount, commands, initialValues, dataRows, dataRowIdField, clientSideIdField, formAction } = this.store[gridFormName];
		let dataRow = Object.assign({}, submittedDataRow, gridFormFieldAPI.inputsValues());
		const dataRowProps = { submittedDataRow: dataRow, dataRows, dataRowIdField, clientSideIdField };
		const result = SubmitRowHandler.execute(gridFormName, dataRowProps, totalCount, commands, initialValues, formAction);

		result
			.then(res => {
				dataRow = res.submittedDataRow;

				if (!!res.result) {
					const currentId = dataRow[clientSideIdField];
					const submittedDataRowIndex = res.result.dataRows.map(data => data.id).indexOf(currentId);

					if(submittedDataRowIndex >= 0)
						res.result.dataRows.splice(submittedDataRowIndex, 1, dataRow);
					else {
						dataRow[clientSideIdField] = generateUUID();
						res.result.dataRows.push(dataRow);
					}

					this.onSetProperties(gridFormName, res.result, false);
					this.updateStoreState(gridFormName);

					if (formAction === 'create') {
						gridFormFieldAPI.publish(FormEvents.GRID_ADD_AFTER, res.submittedDataRow, res.result.dataRows);
					}
					else {
						gridFormFieldAPI.publish(FormEvents.GRID_EDIT_AFTER, res.submittedDataRow, res.result.dataRows);
						this.onReset(gridFormName, true);
					}
				}
			})
			.catch(err => {
				const formApi = ApiUtil.getFormAPI();

				if (typeof err === "string") {
					formApi.errors({ [gridFormName]: err }).apply();
				}
				else if (err) {
					formApi.errors(err).apply();
				}
			});

		setTimeout(() => {
			let buttonDestroyGrid = $('.destroy-line-grid');
			buttonDestroyGrid.off('keypress');
			buttonDestroyGrid.off('focus');

			buttonDestroyGrid.keypress((e) => {
				if(e.which === 13){
					e.target.click();
					e.currentTarget.lastChild.focus();
				}
			})
		}, 1000)
	},

	onLoadInitialValues(initialValues, gridFormName) {
		if (initialValues) {
			const formattedInitialValues = this._formatInitialValues(initialValues);
			this.onSetProperty(gridFormName, 'initialValues', formattedInitialValues, false);
			this.updateStoreState(gridFormName);
		}
		else
			this.updateStoreState(gridFormName);
	},

	_formatInitialValues(initialValues) {
		return initialValues.reduce((acc, { fieldId, value }) => Object.assign({}, acc, { [fieldId]: value }) , {});
	},

	onReset(gridFormName, publishEvent) {
		const gridFormFieldAPI = ApiUtil.getFieldAPI(gridFormName);

		this._reset(gridFormName)
			.then(() => {
				if (publishEvent) {
					gridFormFieldAPI.publish(FormEvents.GRID_RESET, gridFormName);
				}
			});
	},

	async _reset(gridFormName) {
		const { initialValues } = this.store[gridFormName];
		const result = await ResetHandler.execute(gridFormName, initialValues);

		this.onSetProperties(gridFormName, result, true);
	},

	/* Initializer functions */

	subscribeToProcessSaveEvent() {
		ActivityFormEventHandler.subscribe(ActivityFormEvents.ACTIVITY_FORM_SAVE_SUCCESS, () => {
			this.resetAllGridFormCommands();
			this.updateDataRows();
		});
	},

	resetAllGridFormCommands() {
		Object.values(this.store)
		.forEach(storeItem => this.onSetProperty(storeItem.name, 'commands', [], false))
	},

	updateDataRows() {
		Object.keys(this.store).forEach(storeItemKey => {
			this.resetLoadedPages(storeItemKey);
			this.loadPageFromDataStore(storeItemKey, 1);
		});
	},

	resetDataRows(storeItemKey) {
		this.onSetProperty(storeItemKey, 'dataRows', [], false);
	},

	resetLoadedPages(storeItemKey) {
		this.onSetProperty(storeItemKey, 'loadedPages', [], false);
	},

	/* Store getters */

	getState() {
		return this.store;
	},

	/* Page data loading handlers */

	loadPageFromDataStore(gridFormName, page) {
		this._waitForAction(gridFormName, true)
			.then(() => this._loadPageFromDataStore(gridFormName, page));
	},

	_loadPageFromDataStore(gridFormName, page) {
		const { dataStoreRoute } = this.store[gridFormName];

		GridFormValuesLoader
			.load(dataStoreRoute, gridFormName)
			.then(this.handleLoadPageFromDataStoreSuccess.bind(this, gridFormName, page));
	},

	handleLoadPageFromDataStoreSuccess(gridFormName, page, dataStoreResponse) {
		const gridFormStore = this.store[gridFormName];
		const gridFormFieldAPI = ApiUtil.getFieldAPI(gridFormName);

		gridFormFieldAPI.publish(FormEvents.GRID_DATA_ROW_LOADED, dataStoreResponse.resources);

		if (!gridFormStore.totalCountLoaded)
			this.updateTotalCountFromResponse(gridFormStore, dataStoreResponse);

		this._waitForAction(gridFormName, false, false);

		this.onInsertPageData(gridFormName, page, dataStoreResponse.resources, true);
	},

	updateTotalCountFromResponse(gridFormStore, dataStoreResponse) {
		const totalCount = this._extractTotalCountOfDataStoreResponse(dataStoreResponse);
		this.onSetProperties(gridFormStore.name, { totalCount, totalCountLoaded: true }, false);
	},

	_extractTotalCountOfDataStoreResponse(dataStoreResponse) {
		const { page: responsePage } = dataStoreResponse;

		return (!!responsePage && typeof responsePage.totalElements === "number")
			? responsePage.totalElements
			: dataStoreResponse.resources.length;
	},

	/* gridFormStore property handlers */

	addLoadedPage(gridFormStore, page) {
		if (!gridFormStore.loadedPages.includes(page))
			this.onSetProperty(gridFormStore.name, 'loadedPages', [...gridFormStore.loadedPages, page], false);

		this.onSetProperty(gridFormStore.name, 'loadedPages', gridFormStore.loadedPages.sort(), false);
	},

	addDataRows(gridFormStore, dataRows) {
		const dataRowsOld = gridFormStore.dataRows;
		const propsRows = gridFormStore.propsRows;
		let objProps = {};

		for (let key in propsRows) {
			const index = dataRowsOld.map(d => String(d.id)).indexOf(String(key));

			if (dataRows[index] && dataRows[index].id)
				objProps[dataRows[index].id] = propsRows[key];
		}

		this.onSetProperties(gridFormStore.name, { dataRows: dataRows, propsRows: objProps }, false);
	},

	updateStoreState(gridFormName, loadNextPage = false, removeDataRow = false) {
		if (loadNextPage)
			this._loadNextPageDataFromStore(gridFormName);
		else
			this._waitForAction(gridFormName, false);

		this.updateGridPage(gridFormName, removeDataRow);
		this.onSetState(gridFormName);
	},

	updateGridPage(gridFormName, removeDataRow) {
		const storeItem = this.store[gridFormName];
		const isIntegerNumberOfItemsPerPage = Number.isInteger(storeItem.dataRows.length / storeItem.perPage);

		if (storeItem.gridPage > 1 && removeDataRow && isIntegerNumberOfItemsPerPage)
			return this.onSetProperty(gridFormName, 'gridPage', storeItem.gridPage - 1, false);
	},

	onLoadNextPageData({ dataRows, gridPage, gridPerPage }, gridFormName) {
		this._loadNextPageData({ dataRows, gridPage, gridPerPage }, gridFormName);
	},

	_loadNextPageDataFromStore(gridFormName) {
		const { dataRows, gridPage, gridPerPage } = this.store[gridFormName];
		this._loadNextPageData({ dataRows, gridPage, gridPerPage }, gridFormName);
	},

	_loadNextPageData({ dataRows, gridPage, gridPerPage }, gridFormName) {
		const perPage = gridPerPage || 20;
		const currentPageDataRows = PaginationHandler.paginate({ dataRows, gridPage, gridPerPage: perPage});

		this.onSetProperties(gridFormName, { gridPage: gridPage, gridPerPage: perPage }, false);
	},

	/* gridFormStore command handlers */

	_waitForAction(gridFormName, value, trigger = true) {
		return this._setProperty(gridFormName, 'waitingForAction', value, trigger);
	},

	_resetStore() {
		this.store = {}
	},

	onUpdateDataRowSign(gridDataSign, fileUniqueId) {
		const gridFormName = gridDataSign.gridId;
		const gridFieldName = gridDataSign.name;

		if (typeof gridDataSign.value !== 'string') {
			window.gridDataSign = null;
			return;
		}

		const assignedDocumentFileId = gridDataSign.value.split(':')[1];

		const { dataRows } = this.store[gridFormName];

		const targetRow = (dataRows || []).find(dataRow => {
			const [, fileUUID] = dataRow[gridFieldName].split(":");
			return fileUUID === assignedDocumentFileId;
		});

		if (targetRow && targetRow.id) {
			const [value] = targetRow[gridFieldName].split(":");
			const newValue = `${value}:${fileUniqueId}`;

			const updatedFields = {
				[gridFieldName]: newValue
			};

			this.onUpdateDataRow(gridFormName, { ...targetRow, ...updatedFields });
		}

		window.gridDataSign = null;
	},
});

export default GridFormStore;
